'use client'

// ============================================================================
// LandingHeroTabs (Attio Premium v2) — главный экран лендинга
// ============================================================================
// Премиум-стиль: чистая типографика, минимум цветов, фокус на product mockup.
// Сохраняем переключатель ролей Forwarder/Importer.
// ============================================================================

import { Link } from '@/lib/i18n/navigation'
import { useState } from 'react'
import { ListlogistLogo } from '@/components/brand/ListlogistLogo'
import { ArrowRight, Check, Truck, Gavel, Play, Sparkles, Route, Bell } from 'lucide-react'
import { MONO } from '@/lib/marketing/fonts'
import { SLATE } from '@/lib/design/tokens'

// tint() — производные акцента через color-mix (как в дизайн-ките Claude Design)
const tint = (pct: number, mix: 'white' | 'black' = 'white') =>
  `color-mix(in srgb, var(--accent) ${pct}%, ${mix})`

type Role = 'forwarder' | 'importer'
type Locale = 'ru' | 'ro' | 'en' | 'uk'

interface RoleContent {
  badge: string
  badgeNew: string
  title: React.ReactNode
  subtitle: string
  highlights: string[]
}

interface Props {
  locale: string
  ctaPrimary: string
  ctaSecondary: string
  ctaPrimaryHref: string
  ctaSecondaryHref: string
  note: string
  heroImage?: string
}

const CONTENT: Record<Locale, Record<Role, RoleContent>> = {
  ru: {
    forwarder: {
      badge: 'Для экспедиторов',
      badgeNew: 'Новое',
      title: (
        <>
          CRM для перевозок,<br />
          <span className="text-ink-500">построенная для логистов</span>
        </>
      ),
      subtitle: 'Сделки, тендеры, документы и финансы — в одном месте. Listlogist заменяет десять таблиц и работает так, как должна работать современная логистическая компания.',
      highlights: ['Сделки с маршрутами', 'Документы автоматически', 'Telegram для водителей', 'Финансы и аналитика'],
    },
    importer: {
      badge: 'Для импортёров',
      badgeNew: 'Listlogist Tenders',
      title: (
        <>
          Тендеры на перевозки.<br />
          <span className="text-ink-500">Экономия до 30%.</span>
        </>
      ),
      subtitle: 'Listlogist помогает импортёрам и производителям проводить торги среди перевозчиков, выбирать лучшую цену прозрачно и сохранять историю всех решений.',
      highlights: ['Тендер за 2 минуты', 'Email-приглашения участникам', 'Все ставки в одном месте', 'Протокол выбора решения'],
    },
  },
  ro: {
    forwarder: {
      badge: 'Pentru expeditori',
      badgeNew: 'Nou',
      title: (
        <>
          CRM pentru transport,<br />
          <span className="text-ink-500">construit pentru logisticieni</span>
        </>
      ),
      subtitle: 'Tranzacții, licitații, documente și finanțe — într-un singur loc. Listlogist înlocuiește zece tabele.',
      highlights: ['Tranzacții cu rute', 'Documente automate', 'Telegram pentru șoferi', 'Finanțe și analitică'],
    },
    importer: {
      badge: 'Pentru importatori',
      badgeNew: 'Listlogist Tenders',
      title: (
        <>
          Licitații pentru transport.<br />
          <span className="text-ink-500">Economie până la 30%.</span>
        </>
      ),
      subtitle: 'Listlogist ajută importatorii să organizeze licitații între transportatori și să aleagă cel mai bun preț transparent.',
      highlights: ['Licitație în 2 minute', 'Invitații pe email', 'Toate ofertele într-un loc', 'Protocol de decizie'],
    },
  },
  en: {
    forwarder: {
      badge: 'For forwarders',
      badgeNew: 'New',
      title: (
        <>
          Transportation CRM,<br />
          <span className="text-ink-500">built for logistics teams</span>
        </>
      ),
      subtitle: 'Deals, tenders, documents and finance — in one place. Listlogist replaces ten spreadsheets.',
      highlights: ['Deals with routes', 'Automated documents', 'Telegram for drivers', 'Finance and analytics'],
    },
    importer: {
      badge: 'For importers',
      badgeNew: 'Listlogist Tenders',
      title: (
        <>
          Freight tenders.<br />
          <span className="text-ink-500">Save up to 30%.</span>
        </>
      ),
      subtitle: 'Listlogist helps importers run tenders among carriers and pick the best price transparently.',
      highlights: ['Tender in 2 minutes', 'Email invitations', 'All bids in one place', 'Decision protocol'],
    },
  },
  uk: {
    forwarder: {
      badge: 'Для експедиторів',
      badgeNew: 'Нове',
      title: (
        <>
          CRM для перевезень,<br />
          <span className="text-ink-500">побудована для логістів</span>
        </>
      ),
      subtitle: 'Угоди, тендери, документи та фінанси — в одному місці. Listlogist замінює десять таблиць і працює так, як має працювати сучасна логістична компанія.',
      highlights: ['Угоди з маршрутами', 'Документи автоматично', 'Telegram для водіїв', 'Фінанси та аналітика'],
    },
    importer: {
      badge: 'Для імпортерів',
      badgeNew: 'Listlogist Tenders',
      title: (
        <>
          Тендери на перевезення.<br />
          <span className="text-ink-500">Економія до 30%.</span>
        </>
      ),
      subtitle: 'Listlogist допомагає імпортерам і виробникам проводити торги серед перевізників, обирати найкращу ціну прозоро та зберігати історію всіх рішень.',
      highlights: ['Тендер за 2 хвилини', 'Email-запрошення учасникам', 'Усі ставки в одному місці', 'Протокол вибору рішення'],
    },
  },
}

export function LandingHeroTabs({
  locale,
  ctaPrimary,
  ctaSecondary,
  ctaPrimaryHref,
  ctaSecondaryHref,
  note,
  heroImage,
}: Props) {
  const loc = (['ru', 'ro', 'en', 'uk'].includes(locale) ? locale : 'ru') as Locale
  const [role, setRole] = useState<Role>('forwarder')
  const c = CONTENT[loc][role]

  const tabLabel = {
    forwarder: { ru: 'Экспедиторы', ro: 'Expeditori', en: 'Forwarders', uk: 'Експедитори' }[loc],
    importer:  { ru: 'Импортёры',   ro: 'Importatori', en: 'Importers', uk: 'Імпортери' }[loc],
  }

  return (
    <section className="relative px-6 pt-16 pb-20 max-w-6xl mx-auto overflow-hidden">
      {/* Радиальная подсветка акцентом в правом-верхнем углу */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10 pointer-events-none"
        style={{ background: 'radial-gradient(55% 45% at 80% 0%, color-mix(in srgb, var(--accent) 10%, transparent), transparent 70%)' }}
      />

      {/* Pill переключатель ролей */}
      <div className="flex justify-center mb-10">
        <div role="tablist" className="inline-flex items-center gap-0.5 p-1 rounded-full bg-ink-100/70 border-hairline border-ink-200/80 backdrop-blur-sm">
          <button
            role="tab" aria-selected={role === 'forwarder'} type="button"
            onClick={() => setRole('forwarder')}
            className={'inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ' +
              (role === 'forwarder' ? 'bg-white text-ink-900 shadow-sm' : 'text-ink-500 hover:text-ink-900')}
          >
            <Truck size={13} strokeWidth={1.75} />
            {tabLabel.forwarder}
          </button>
          <button
            role="tab" aria-selected={role === 'importer'} type="button"
            onClick={() => setRole('importer')}
            className={'inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ' +
              (role === 'importer' ? 'bg-white text-ink-900 shadow-sm' : 'text-ink-500 hover:text-ink-900')}
          >
            <Gavel size={13} strokeWidth={1.75} />
            {tabLabel.importer}
          </button>
        </div>
      </div>

      {/* Двухколоночный grid: текст слева, продуктовая карточка справа */}
      <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-14 items-center">
        {/* ЛЕВО */}
        <div>
          {/* Бейдж-новинка */}
          <Link
            href={`/for-${role}s`}
            className="group inline-flex items-center gap-2 py-1 pr-3 pl-1.5 rounded-full bg-white border-hairline border-ink-200 text-xs text-ink-500 hover:border-ink-250 transition-colors mb-6"
          >
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-white text-[10px] font-medium tracking-wide uppercase"
              style={{ backgroundColor: 'var(--accent)', fontFamily: 'var(--font-landing-mono), monospace' }}
            >
              <Sparkles size={9} strokeWidth={2} />
              {c.badgeNew}
            </span>
            <span className="text-ink-700">{c.badge}</span>
            <ArrowRight size={11} className="text-ink-400 group-hover:translate-x-0.5 transition-transform" />
          </Link>

          {/* Заголовок */}
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-[-0.03em] text-ink-900 leading-[1.08]">
            {c.title}
          </h1>

          {/* Подзаголовок */}
          <p className="mt-5 text-base md:text-lg text-ink-500 max-w-xl leading-relaxed">
            {c.subtitle}
          </p>

          {/* CTA */}
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              href={`${ctaPrimaryHref}${ctaPrimaryHref.includes('?') ? '&' : '?'}role=${role}`}
              className="group inline-flex items-center gap-2 rounded-[11px] text-white text-sm font-semibold px-5 py-3 transition-all shadow-sm hover:-translate-y-0.5"
              style={{ backgroundColor: 'var(--accent)' }}
            >
              {ctaPrimary}
              <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
            </Link>
            <Link
              href={ctaSecondaryHref}
              className="inline-flex items-center gap-2 rounded-[11px] border-hairline border-ink-250 hover:border-ink-300 hover:bg-ink-50 text-ink-700 text-sm font-medium px-4 py-3 transition-colors"
            >
              <Play size={12} strokeWidth={2} />
              {ctaSecondary}
            </Link>
          </div>

          <p className="mt-3 text-xs text-ink-400">{note}</p>

          {/* Чипы возможностей */}
          <div className="mt-8 pt-6 border-t-hairline border-ink-200 flex flex-wrap gap-x-5 gap-y-2.5 text-[13px] text-ink-600">
            {c.highlights.map((h, i) => (
              <span key={i} className="inline-flex items-center gap-1.5">
                <Check size={13} strokeWidth={2.5} style={{ color: 'var(--accent)' }} />
                {h}
              </span>
            ))}
          </div>
        </div>

        {/* ПРАВО — продуктовая карточка */}
        <div className="relative">
          <div
            aria-hidden
            className="absolute -inset-6 -z-10 rounded-2xl blur-2xl opacity-50"
            style={{ background: 'radial-gradient(closest-side, color-mix(in srgb, var(--accent) 14%, transparent), transparent)' }}
          />
          {heroImage ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={heroImage} alt="Listlogist" className="w-full rounded-lg border-hairline border-ink-200 shadow-2xl bg-white" />
          ) : (
            <ProductMockupPreview role={role} loc={loc} />
          )}
        </div>
      </div>
    </section>
  )
}


// ============================================================================
// Продуктовые карточки Hero — точный перенос дизайн-кита Claude Design
// (DealCard для экспедиторов, TenderCard для импортёров).
// ============================================================================

function ProductMockupPreview({ role, loc }: { role: Role; loc: Locale }) {
  return role === 'forwarder' ? <DealCard loc={loc} /> : <TenderCard loc={loc} />
}

// Локализованные подписи продуктовых карточек Hero (демо-данные — компании,
// ID и URL — намеренно остаются неизменными как технические/брендовые).
const CARD_I18N: Record<Locale, {
  docContract: string
  docInvoice: string
  inTransit: string
  dealRoute: string
  updatedAgo: string
  client: string
  carrier: string
  tenderHeader: string
  tenderRoute: string
  tenderMeta: string
  best: string
  distance: string
  days: (n: number) => string
}> = {
  ru: {
    docContract: 'Договор',
    docInvoice: 'Счёт',
    inTransit: 'В пути',
    dealRoute: 'Кишинёв → Берлин',
    updatedAgo: 'Обновлено 2 мин назад',
    client: 'Заказчик',
    carrier: 'Перевозчик',
    tenderHeader: 'ТЕНДЕР #2041 · ЗАКРЫТЫЙ',
    tenderRoute: 'Кишинёв → Бухарест',
    tenderMeta: '10 перевозчиков · ставки приватны · дедлайн 18:00',
    best: 'Лучшая',
    distance: '2 100 км',
    days: (n) => `${n} ${n === 1 ? 'день' : n < 5 ? 'дня' : 'дней'}`,
  },
  ro: {
    docContract: 'Contract',
    docInvoice: 'Factură',
    inTransit: 'Pe drum',
    dealRoute: 'Chișinău → Berlin',
    updatedAgo: 'Actualizat acum 2 min',
    client: 'Client',
    carrier: 'Transportator',
    tenderHeader: 'LICITAȚIE #2041 · ÎNCHISĂ',
    tenderRoute: 'Chișinău → București',
    tenderMeta: '10 transportatori · oferte private · termen 18:00',
    best: 'Cea mai bună',
    distance: '2 100 km',
    days: (n) => `${n} zile`,
  },
  en: {
    docContract: 'Contract',
    docInvoice: 'Invoice',
    inTransit: 'In transit',
    dealRoute: 'Chisinau → Berlin',
    updatedAgo: 'Updated 2 min ago',
    client: 'Client',
    carrier: 'Carrier',
    tenderHeader: 'TENDER #2041 · CLOSED',
    tenderRoute: 'Chisinau → Bucharest',
    tenderMeta: '10 carriers · bids are private · deadline 18:00',
    best: 'Best',
    distance: '2,100 km',
    days: (n) => `${n} ${n === 1 ? 'day' : 'days'}`,
  },
  uk: {
    docContract: 'Договір',
    docInvoice: 'Рахунок',
    inTransit: 'В дорозі',
    dealRoute: 'Кишинів → Берлін',
    updatedAgo: 'Оновлено 2 хв тому',
    client: 'Замовник',
    carrier: 'Перевізник',
    tenderHeader: 'ТЕНДЕР #2041 · ЗАКРИТИЙ',
    tenderRoute: 'Кишинів → Бухарест',
    tenderMeta: '10 перевізників · ставки приватні · дедлайн 18:00',
    best: 'Найкраща',
    distance: '2 100 км',
    days: (n) => `${n} ${n === 1 ? 'день' : n < 5 ? 'дні' : 'днів'}`,
  },
}

function DealCard({ loc }: { loc: Locale }) {
  const t = CARD_I18N[loc]
  const docs = ['CMR', t.docContract, t.docInvoice]
  return (
    <div style={{ background: '#fff', borderRadius: 18, boxShadow: '0 24px 60px -20px rgba(15,23,42,0.30), 0 0 0 1px rgba(15,23,42,0.06)', overflow: 'hidden' }}>
      <div style={{ background: SLATE[900], padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <ListlogistLogo variant="full" size="sm" tone="dark" />
        <span style={{ marginLeft: 'auto', fontFamily: MONO, fontSize: 11.5, color: SLATE[400] }}>listlogist.com/deals/MD-2026-0142</span>
      </div>
      <div style={{ padding: 22 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontFamily: MONO, fontSize: 12, fontWeight: 600, color: SLATE[400], letterSpacing: '0.04em' }}>MD-2026-0142</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, fontWeight: 600, color: '#fff', background: 'var(--accent)', padding: '3px 9px', borderRadius: 999, whiteSpace: 'nowrap' }}>
            <span style={{ width: 5, height: 5, borderRadius: 999, background: '#fff' }} />{t.inTransit}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
          <span style={{ color: 'var(--accent)', display: 'inline-flex' }}><Route size={22} strokeWidth={1.7} /></span>
          <span style={{ fontWeight: 800, fontSize: 21, color: SLATE[900], letterSpacing: '-0.02em', whiteSpace: 'nowrap' }}>{t.dealRoute}</span>
          <span style={{ fontFamily: MONO, fontSize: 13, color: SLATE[400], marginLeft: 'auto', whiteSpace: 'nowrap' }}>{t.distance}</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 18 }}>
          <div style={{ background: SLATE[50], borderRadius: 12, padding: '13px 15px', border: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: 11.5, color: SLATE[500] }}>{t.client}</div>
            <div style={{ fontWeight: 700, fontSize: 15, color: SLATE[900], marginTop: 3 }}>AgroMD SRL</div>
            <div style={{ fontFamily: MONO, fontWeight: 600, fontSize: 16, color: SLATE[900], marginTop: 6 }}>€2 800</div>
          </div>
          <div style={{ background: SLATE[50], borderRadius: 12, padding: '13px 15px', border: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: 11.5, color: SLATE[500] }}>{t.carrier}</div>
            <div style={{ fontWeight: 700, fontSize: 15, color: SLATE[900], marginTop: 3 }}>EuroTrans GmbH</div>
            <div style={{ fontFamily: MONO, fontWeight: 600, fontSize: 16, color: SLATE[900], marginTop: 6 }}>€2 100 <span style={{ color: tint(75, 'black'), fontSize: 13 }}>+€700</span></div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16 }}>
          {docs.map((d) => (
            <span key={d} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: SLATE[600], background: tint(8), border: `1px solid ${tint(18)}`, padding: '6px 11px', borderRadius: 8 }}>
              <Check size={13} strokeWidth={2.4} />{d}
            </span>
          ))}
          <span style={{ marginLeft: 'auto', fontSize: 11.5, color: SLATE[400] }}>{t.updatedAgo}</span>
        </div>
      </div>
    </div>
  )
}

function TenderCard({ loc }: { loc: Locale }) {
  const t = CARD_I18N[loc]
  const bids: Array<[string, string, string, boolean]> = [
    ['ТрансКарго', '€1 240', t.days(3), true],
    ['Via Logistic', '€1 380', t.days(2), false],
    ['EuroSped', '€1 410', t.days(4), false],
  ]
  return (
    <div style={{ background: '#fff', borderRadius: 18, boxShadow: '0 24px 60px -20px rgba(15,23,42,0.30), 0 0 0 1px rgba(15,23,42,0.06)', overflow: 'hidden' }}>
      <div style={{ background: SLATE[900], padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <ListlogistLogo variant="full" size="sm" tone="dark" />
        <span style={{ marginLeft: 'auto', fontFamily: MONO, fontSize: 11.5, color: SLATE[400] }}>listlogist.com/tenders/2041</span>
      </div>
      <div style={{ padding: 22 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <div style={{ fontFamily: MONO, fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', color: SLATE[400] }}>{t.tenderHeader}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginTop: 6 }}>
              <span style={{ color: 'var(--accent)', display: 'inline-flex' }}><Route size={20} strokeWidth={1.7} /></span>
              <span style={{ fontWeight: 800, fontSize: 19, color: SLATE[900], letterSpacing: '-0.02em' }}>{t.tenderRoute}</span>
            </div>
            <div style={{ fontSize: 12.5, color: SLATE[500], marginTop: 5 }}>{t.tenderMeta}</div>
          </div>
          <span style={{ color: SLATE[300], flex: '0 0 auto', display: 'inline-flex' }}><Bell size={20} strokeWidth={1.7} /></span>
        </div>
        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {bids.map(([name, price, eta, best]) => (
            <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 13px', borderRadius: 11, background: best ? tint(9) : SLATE[50], boxShadow: best ? `inset 0 0 0 1.5px var(--accent)` : `inset 0 0 0 1px #e2e8f0` }}>
              <ListlogistLogo variant="mark" size="sm" accent={best ? undefined : SLATE[400]} tone={best ? 'light' : 'light'} />
              <span style={{ fontWeight: 600, fontSize: 14, color: SLATE[800], flex: 1 }}>{name}</span>
              <span style={{ fontFamily: MONO, fontSize: 13, color: SLATE[500], whiteSpace: 'nowrap' }}>{eta}</span>
              <span style={{ fontFamily: MONO, fontWeight: 600, fontSize: 15, color: best ? tint(78, 'black') : SLATE[800], width: 70, textAlign: 'right' }}>{price}</span>
              {best
                ? <span style={{ fontWeight: 700, fontSize: 10.5, color: '#fff', background: 'var(--accent)', padding: '4px 8px', borderRadius: 999 }}>{t.best}</span>
                : <span style={{ width: 50 }} />}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
